import React, { useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import { DialogTitle } from '../components/DialogTitle';
import { DialogContentWithCards } from '../components/DialogContent';
import { TextLabel } from '../components/TextLabel';
import { capitalize, timeStampToLongString } from '../utils/stringUtils';
import SimpleTable from '../components/SimpleTable';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import ListTable from '../components/ListTable';
import { AppCells } from '../components/AppCells';
import { Card } from '../components/Card';
import { PaperCard } from '../components/PaperCard';
import { ramToString, availableStorageToString, installedAppsToRows } from '../utils/deviceDialogUtils';
import { Progress } from '../components/Progress';
import LocationMap from '../components/LocationMap';
import { getDeviceGroupName, getDevicePUIVersion, getDeviceLastCheckInString, getDeviceOrganizationName, getDevicePicoControllerVersion } from '../utils/deviceUtils';
import { AreYouSureDialog } from '../dialogs/AreYouSureDialog';
import { AreYouSureDialogWithTextRequirement } from './AreYouSureDialogWithTextRequirement';
import { deleteDevice } from '../redux/actions/deviceActions';
import { useDispatch } from 'react-redux';

export const DeviceInfoDialog = ({ onClose, onEdit, device, deviceGroup, approvedApps, installedApps, loading, onDeviceComandsClick }) => {
    let {
        name,
        serial,
        osVersion,
        osDisplayVersion,
        model,
        manufacturer,
        mac,
        availableStorage,
        ram,
        batteryLevel,
        lastUpdated,
        kioskModeEnabled = false,
        firstTimeUseModeEnabled = false,
        location,
        notes,
        organizationRef,
        deviceOwnerEnabled,
    } = device;

    let [showAreYouSureDialog, setShowAreYouSureDialog] = useState(false);
    let dispatch = useDispatch();

    let deviceDetailsRows = [['Model', model], ['Manufacturer', manufacturer], ['OS Version', osVersion]];

    if (manufacturer === 'Pico') {
        deviceDetailsRows.push(['PUI Version', osDisplayVersion]);
        deviceDetailsRows.push(['Pico Controller Versions', getDevicePicoControllerVersion(device)]);
    }
    deviceDetailsRows.push(['Serial', serial]);
    deviceDetailsRows.push(['MAC', mac]);

    return (
        <>
            {showAreYouSureDialog ? (
                <AreYouSureDialogWithTextRequirement
                    onClose={() => setShowAreYouSureDialog(false)}
                    onDelete={() => {
                        setShowAreYouSureDialog(false);
                        onClose();
                        deleteDevice({ serial, dispatch });
                    }}
                    title={`Delete Device "${name}"?`}
                    textRequirement={name}
                >
                    <p>You're about to delete device "{name}" from the Mighty MDM platform. There are few things you should know:</p>
                    <ul>
                        <li>Once a device is removed from the platform, the Mighty MDM Admin App on the device will cease to function.</li>
                        <li>
                            It is recommended that you uninstall the Mighty MDM from the device before deleting it from the platform. You can do this by factory reseting your device, or using the
                            Mighty MDM Device Setup tool to run the uninstall process.
                        </li>
                        <li>You can re-register the device at any time using the Mighty MDM Device Setup tool.</li>
                    </ul>
                </AreYouSureDialogWithTextRequirement>
            ) : (
                <Dialog maxWidth="lg" scroll="body" onClose={onClose} open spacing={0}>
                    <DialogTitle onEdit={onEdit} onDeviceComandsClick={onDeviceComandsClick} onDelete={() => setShowAreYouSureDialog(true)} darkBg organizationRef={organizationRef}>
                        Device: {name}
                    </DialogTitle>
                    <DialogContentWithCards>
                        {loading && <Progress />}
                        <Grid container spacing={3}>
                            <Grid item xs={6}>
                                <Card>
                                    <Typography variant="h6">Managed Details</Typography>
                                    <TextLabel label="Name" value={name} />
                                    <TextLabel label="Device Group" value={deviceGroup.name} />
                                    <TextLabel label="Notes" value={notes} multiline rowsMax="2" />
                                </Card>
                            </Grid>
                            <Grid item xs={6}>
                                <Card>
                                    <Typography variant="h6">Hardware Details</Typography>
                                    <ListTable rows={deviceDetailsRows} />
                                </Card>
                            </Grid>
                            <Grid item xs={6}>
                                <Card>
                                    <Typography variant="h6">Device Status</Typography>
                                    <ListTable
                                        rows={[
                                            ['Last Check-In', timeStampToLongString(lastUpdated)],
                                            ['Kiosk Mode', kioskModeEnabled ? 'Enabled' : 'Disabled'],
                                            ['Device Owner', deviceOwnerEnabled ? 'Enabled' : 'Disabled'],
                                            ['First Time Use Mode', firstTimeUseModeEnabled ? 'Enabled' : 'Disabled'],
                                            ['Battery Level', batteryLevel ? batteryLevel + '%' : ''],
                                            ['Available Storage', availableStorageToString(availableStorage)],
                                            ['RAM Usage', ramToString(ram)],
                                        ]}
                                    />
                                </Card>
                            </Grid>
                            <Grid item xs={6}>
                                <Card>
                                    <Typography variant="h6">Last Location</Typography>
                                    <LocationMap location={location} />
                                </Card>
                            </Grid>
                            <Grid item xs={12}>
                                <PaperCard>
                                    <Typography variant="h6">Approved Apps</Typography>
                                    <AppCells apps={approvedApps} installedApps={installedApps} />
                                </PaperCard>
                            </Grid>
                            <Grid item xs={12}>
                                <PaperCard>
                                    <Typography variant="h6">Installed Apps</Typography>
                                    <SimpleTable headers={['Name', 'Package Name', 'Version Code', 'Version Name', 'Last Updated']} rows={installedAppsToRows(installedApps)} />
                                </PaperCard>
                            </Grid>
                        </Grid>
                        <br />
                    </DialogContentWithCards>
                </Dialog>
            )}
        </>
    );
};
